import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { createAutomation, deleteAutomation, getAutonomyProfile, getAutomations, setAutomationEnabled } from "@/services/api";

export const automationTriggerLabel = (rule) => {
  const condition = rule?.if || {};
  if (condition.presence === "home") return "Wenn jemand nach Hause kommt";
  if (condition.presence === "away") return "Wenn niemand zuhause ist";
  if (condition.time === "morning") return "Morgens";
  if (condition.time === "evening") return "Abends";
  if (condition.time === "night") return "Nachts";
  if (condition.time) return `Um ${condition.time} Uhr`;
  return "Kein Trigger";
};

export const buildRoutinePayload = ({ name, triggerMode, time, scene }) => {
  const condition = triggerMode === "home" || triggerMode === "away"
    ? { presence: triggerMode }
    : { time: triggerMode === "time" ? time : triggerMode };
  return {
    name: String(name || "").trim(),
    enabled: false,
    if: condition,
    then: { action: "activate_scene", scene },
  };
};

export const canManageAutomations = (profile) => Array.isArray(profile?.allowed_capabilities) && profile.allowed_capabilities.includes("AUTOMATION_CONTROL");

export const AionAutomationPanel = ({ token, scenes = [], profileAccessMode = "self", onChanged }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({ name: "", triggerMode: "morning", time: "07:00", scene: "" });
  const [access, setAccess] = useState({ loading: profileAccessMode !== "shared_view", canManage: false, error: "" });
  const locked = profileAccessMode === "shared_view" || !access.canManage;

  useEffect(() => {
    let cancelled = false;
    if (profileAccessMode === "shared_view") {
      setAccess({ loading: false, canManage: false, error: "" });
      return () => { cancelled = true; };
    }

    setAccess({ loading: true, canManage: false, error: "" });
    void getAutonomyProfile(token)
      .then((profile) => {
        if (!cancelled) setAccess({ loading: false, canManage: canManageAutomations(profile), error: "" });
      })
      .catch((accessError) => {
        if (!cancelled) setAccess({ loading: false, canManage: false, error: accessError?.message || "Berechtigungen konnten nicht geprüft werden." });
      });

    return () => { cancelled = true; };
  }, [profileAccessMode, token]);

  const sceneNames = useMemo(() => scenes.map((scene) => scene?.name).filter(Boolean), [scenes]);

  useEffect(() => {
    if (!draft.scene && sceneNames[0]) setDraft((previous) => ({ ...previous, scene: sceneNames[0] }));
  }, [draft.scene, sceneNames]);

  const loadRules = useCallback(async () => {
    if (locked) { setRules([]); setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      setRules(await getAutomations(token));
    } catch (loadError) {
      setError(loadError?.message || "Automationen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [locked, token]);

  useEffect(() => { void loadRules(); }, [loadRules]);

  const createRoutine = async () => {
    const payload = buildRoutinePayload(draft);
    if (!payload.name || !payload.then.scene) {
      setError("Name und vorhandene Szene sind erforderlich.");
      return;
    }
    setBusyId("create");
    setError("");
    try {
      await createAutomation(payload, token);
      setDraft((previous) => ({ ...previous, name: "" }));
      await loadRules();
      await onChanged?.();
    } catch (saveError) {
      setError(saveError?.message || "Routine konnte nicht gespeichert werden.");
    } finally {
      setBusyId("");
    }
  };

  const toggleRule = async (rule) => {
    setBusyId(rule.id);
    setError("");
    try {
      await setAutomationEnabled(rule.id, !rule.enabled, token);
      await loadRules();
      await onChanged?.();
    } catch (toggleError) {
      setError(toggleError?.message || "Routine konnte nicht geändert werden.");
    } finally {
      setBusyId("");
    }
  };

  const removeRule = async (rule) => {
    setBusyId(rule.id);
    setError("");
    try {
      await deleteAutomation(rule.id, token);
      await loadRules();
      await onChanged?.();
    } catch (deleteError) {
      setError(deleteError?.message || "Routine konnte nicht gelöscht werden.");
    } finally {
      setBusyId("");
    }
  };

  if (access.loading) return <AionEmptyState title="Berechtigungen werden geprüft" description="AION bestätigt zuerst dein aktuelles Autonomy-Profil. Bis dahin bleiben Routinen schreibgeschützt." testId="automation-access-loading" />;
  if (locked) return <AionEmptyState title="Routinen schreibgeschützt" description={profileAccessMode === "shared_view" ? "Im Familien-Shared-View bleiben persönliche Automationen schreibgeschützt." : access.error ? "AION konnte deine Berechtigung nicht sicher bestätigen. Routinen bleiben deshalb schreibgeschützt." : "Dein aktuelles Autonomy-Profil erlaubt keine dauerhaften Automationen."} testId="automation-shared-profile-locked" />;

  return (
    <div className="aion-view aion-view-stack" data-testid="automation-panel">
      <AionSectionCard title="Familien- & Haushaltsroutinen" eyebrow="AUTOMATIONEN" subtitle="Vorhandene Szenen mit Zeit- oder Presence-Triggern. Neue Routinen starten deaktiviert." action={<Button size="sm" variant="outline" onClick={loadRules} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>} testId="automation-builder-card">
        <div className="aion-settings-grid">
          <label className="aion-settings-field"><span>Name</span><Input value={draft.name} onChange={(event) => setDraft((previous) => ({ ...previous, name: event.target.value }))} placeholder="Morgenroutine" data-testid="automation-name-input" /></label>
          <label className="aion-settings-field"><span>Trigger</span><select value={draft.triggerMode} onChange={(event) => setDraft((previous) => ({ ...previous, triggerMode: event.target.value }))} data-testid="automation-trigger-select"><option value="morning">Morgens</option><option value="evening">Abends</option><option value="night">Nachts</option><option value="home">Ankunft zuhause</option><option value="away">Haus verlassen</option><option value="time">Bestimmte Uhrzeit</option></select></label>
          {draft.triggerMode === "time" ? <label className="aion-settings-field"><span>Uhrzeit</span><Input type="time" value={draft.time} onChange={(event) => setDraft((previous) => ({ ...previous, time: event.target.value }))} data-testid="automation-time-input" /></label> : null}
          <label className="aion-settings-field"><span>Szene</span><select value={draft.scene} onChange={(event) => setDraft((previous) => ({ ...previous, scene: event.target.value }))} data-testid="automation-scene-select"><option value="">Szene wählen</option>{sceneNames.map((name) => <option key={name} value={name}>{name}</option>)}</select></label>
        </div>
        <Button onClick={createRoutine} disabled={busyId === "create" || !sceneNames.length} data-testid="automation-create-button"><Plus size={16} /> Deaktiviert speichern</Button>
        {!sceneNames.length ? <div className="aion-note">Erstelle zuerst mindestens eine Szene. Routinen aktivieren nur vorhandene Szenen.</div> : null}
        {error ? <div className="aion-error-box" data-testid="automation-error">{error}</div> : null}
      </AionSectionCard>

      <AionSectionCard title="Meine Routinen" eyebrow="ROUTINEN" subtitle="Owner-gebundene Automationen aus der bestehenden Engine." testId="automation-list-card">
        {loading ? <div className="aion-note">Routinen werden geladen …</div> : rules.length ? <div className="aion-list">{rules.map((rule) => <div className="aion-list-row aion-list-row-actions" key={rule.id} data-testid={`automation-rule-${rule.id}`}><div><strong>{rule.name}</strong><small>{automationTriggerLabel(rule)} · Szene: {rule.then?.scene || "—"} · {rule.enabled ? "Aktiv" : "Deaktiviert"}</small></div><div className="aion-inline-actions"><Button size="sm" variant={rule.enabled ? "outline" : "default"} onClick={() => toggleRule(rule)} disabled={busyId === rule.id}>{rule.enabled ? "Deaktivieren" : "Aktivieren"}</Button><Button size="sm" variant="ghost" onClick={() => removeRule(rule)} disabled={busyId === rule.id}><Trash2 size={16} /> Löschen</Button></div></div>)}</div> : <AionEmptyState title="Noch keine Routinen" description="Erstelle oben eine Routine aus einer vorhandenen Szene." testId="automation-list-empty" />}
      </AionSectionCard>
    </div>
  );
};
